import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { NbDialogRef } from "@nebular/theme";
import { Subject } from "rxjs";
import { map, takeUntil } from "rxjs/operators";
import { environment } from "../../../environments/environment";
import { UserService } from "../../@core/services/user.service";
import { convertTimestamp } from "../../@core/utils/convertTimestamps.service";
import { ProductInventory } from "../../models/product.model";
import { Receipt, ReceiptDetail } from "../../models/receipt.model";
import { ProductInventoryService } from "../../services/product-inventory.service";
import { PurchaseDetail } from "../../models/purchase.model";

@Component({
  selector: "ngx-dialog-receipt-trade-histories",
  templateUrl: "dialog-receipt-trade-histories.component.html",
  styleUrls: ["dialog-receipt-trade-histories.component.scss"],
})
export class DialogReceiptTradeHistoriesComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  private destroy$: Subject<void> = new Subject<void>();
  @Input() header: string;
  @Input() shopId?: string;
  @Input() uid?: string;
  @Input() productId: string = "";
  @Input() customerId: string = "";
  @Input() supplierId: string = "";
  @Input() isReceive: boolean = false;
  @Input() receiptDetail?: ReceiptDetail;
  @Input() purchaseDetail?: PurchaseDetail;

  loading = false;
  submitted = false;
  strSearch = "";
  mode = "";
  inventories: ProductInventory[] = [];

  discountPercent: number = 0;
  discountAmount: number = 0;
  quantity: number = 0;
  price: number = 0;
  amount: number = 0;
  netAmount: number = 0;
  comment: string = "";

  formatCurrency: string = environment.formatCurrency
    ? environment.formatCurrency
    : "#,###,###,###";
  roundToTwoDecimals = environment.formatCurrency.includes("###.##");

  constructor(
    protected ref: NbDialogRef<DialogReceiptTradeHistoriesComponent>,
    private inventoryService: ProductInventoryService,
  ) {}

  ngOnInit() {
    if (this.isReceive) {
      this.mode = "receive";
      this.quantity = this.purchaseDetail.quantity;
      this.price = this.purchaseDetail.price;
      this.amount = this.purchaseDetail.amount;
      this.netAmount = this.purchaseDetail.netAmount;
      this.discountPercent = this.purchaseDetail.discountPercent;
      this.discountAmount = this.purchaseDetail.discountAmount;
      this.comment = this.purchaseDetail.comment;
    } else {
      this.mode = "issue";
      this.quantity = this.receiptDetail.quantity;
      this.price = this.receiptDetail.price;
      this.amount = this.receiptDetail.amount;
      this.netAmount = this.receiptDetail.netAmount;
      this.discountPercent = this.receiptDetail.discountPercent;
      this.discountAmount = this.receiptDetail.discountAmount;
      this.comment = this.receiptDetail.comment;
    }

    this.getInventories();
  }

  ngAfterViewInit() {
    setTimeout(() => {}, 500);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getInventories() {
    this.loading = true;
    this.inventoryService
      .getProductInventoriesByTrade(
        this.shopId,
        this.productId,
        this.isReceive,
        this.supplierId,
        this.customerId,
      )
      .snapshotChanges(["added", "removed"])
      .pipe(
        map((changes) =>
          changes.map((c) => ({
            id: c.payload.doc.id,
            ...c.payload.doc.data(),
            issueDate: convertTimestamp(c.payload.doc.data().issueDate),
          })),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe((data) => {
        console.log(
          "DialogReceiptTradeHistoriesComponent/getInventories:",
          data,
        );

        this.inventories = data;
        this.loading = false;
      });
  }

  cancel() {
    this.ref.close();
  }

  submit() {
    this.submitted = true;

    if (this.isReceive) {
      this.purchaseDetail.quantity = this.quantity
        ? parseFloat(this.quantity.toString())
        : 0;
      this.purchaseDetail.price = this.price
        ? parseFloat(this.price.toString())
        : 0;
      this.purchaseDetail.netAmount = this.netAmount
        ? parseFloat(this.netAmount.toString())
        : 0;
      this.purchaseDetail.discountPercent = this.discountPercent
        ? parseFloat(this.discountPercent.toString())
        : 0;
      this.purchaseDetail.discountAmount = this.discountAmount
        ? parseFloat(this.discountAmount.toString())
        : 0;
      this.purchaseDetail.amount = this.amount
        ? parseFloat(this.amount.toString())
        : 0;

      this.purchaseDetail.comment = this.comment;
      this.ref.close(this.purchaseDetail);
    } else {
      this.receiptDetail.quantity = this.quantity
        ? parseFloat(this.quantity.toString())
        : 0;
      this.receiptDetail.price = this.price
        ? parseFloat(this.price.toString())
        : 0;
      this.receiptDetail.netAmount = this.netAmount
        ? parseFloat(this.netAmount.toString())
        : 0;
      this.receiptDetail.discountPercent = this.discountPercent
        ? parseFloat(this.discountPercent.toString())
        : 0;
      this.receiptDetail.discountAmount = this.discountAmount
        ? parseFloat(this.discountAmount.toString())
        : 0;
      this.receiptDetail.amount = this.amount
        ? parseFloat(this.amount.toString())
        : 0;
      //cost
      this.receiptDetail.costPrice = this.receiptDetail.costPrice
        ? parseFloat(this.receiptDetail.costPrice.toString())
        : 0;

      this.receiptDetail.costAmount =
        this.receiptDetail.quantity * this.receiptDetail.costPrice;

      this.receiptDetail.profit =
        this.receiptDetail.amount - this.receiptDetail.costAmount;
      this.receiptDetail.comment = this.comment;
      this.ref.close(this.receiptDetail);
    }
  }

  selectedLastPrice(item: ProductInventory) {
    if (this.isReceive) {
      this.price = item.receivePrice;
    } else {
      this.price = item.issuePrice;
    }

    this.summaryDetail();
  }

  productQuantityIncrement(value) {
    this.quantity += value;
    this.summaryDetail();
  }

  summaryDetail() {
    this.price = this.price ? parseFloat(this.price.toString()) : 0;

    this.netAmount = this.quantity * this.price;

    if (this.discountPercent != 0) {
      let discountAmount = (this.netAmount * this.discountPercent) / 100;
      discountAmount = ((discountAmount + Number.EPSILON) * 100) / 100;

      this.discountAmount = this.roundToTwoDecimals
        ? parseFloat(discountAmount.toFixed(2))
        : Math.round(discountAmount);
    }

    this.amount = this.roundToTwoDecimals
      ? parseFloat((this.netAmount - this.discountAmount).toFixed(2))
      : Math.round(this.netAmount - this.discountAmount);
  }

  onInputDiscountAmountChange() {
    this.discountPercent = 0;
    this.summaryDetail();
  }
}
